import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  UserIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { vendorService, ContractRequest, ContractRequestStats } from '../services/vendor.service';

// Static color configurations - defined outside component to avoid recreation
const STATUS_COLORS = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
} as const;

const URGENCY_BADGE_COLORS = {
  success: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  warning: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  danger: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200 dark:bg-secondary-900/20 dark:text-secondary-300 dark:border-secondary-800',
  primary: 'bg-primary-100 text-primary-800 border-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800',
} as const;

// Animation variants - defined outside component to avoid recreation
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

function isOverdue(targetDate: string | undefined | null): boolean {
  if (!targetDate) {return false;}
  return new Date(targetDate) < new Date();
}

function ContractRequestCard({ request }: { request: ContractRequest }) {
  const statusConfig = vendorService.getRequestStatusConfig(request.status);
  const requestTypeConfig = vendorService.getRequestTypeLabel(request.requestType);
  const urgencyConfig = vendorService.getUrgencyConfig(request.urgency);
  const overdue = request.status !== 'COMPLETE' && isOverdue(request.targetCompletionDate);

  return (
    <Card hover="default" padding="none" className="transition-all duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                {request.requestNumber}
              </h3>
              <span
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-full',
                  STATUS_COLORS[statusConfig.color as keyof typeof STATUS_COLORS] ?? STATUS_COLORS.secondary
                )}
              >
                {statusConfig.label}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-full',
              STATUS_COLORS[requestTypeConfig.color as keyof typeof STATUS_COLORS] ?? STATUS_COLORS.secondary
            )}
          >
            {requestTypeConfig.label}
          </span>
          <span
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-full border',
              URGENCY_BADGE_COLORS[urgencyConfig.color as keyof typeof URGENCY_BADGE_COLORS] ?? URGENCY_BADGE_COLORS.secondary
            )}
          >
            <ClockIcon className="w-3 h-3 inline mr-1" />
            {urgencyConfig.label}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <UserIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
            <span className="text-secondary-600 dark:text-secondary-400">Requestor:</span>
            <span className="font-medium text-secondary-900 dark:text-white truncate">
              {request.requestorName}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <EnvelopeIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
            <span className="text-secondary-600 dark:text-secondary-400">Email:</span>
            <span className="font-medium text-secondary-900 dark:text-white truncate">
              {request.requestorEmail}
            </span>
          </div>
          {request.vendorName && (
            <div className="flex items-center space-x-2">
              <BuildingOffice2Icon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Vendor:</span>
              <span className="font-medium text-secondary-900 dark:text-white truncate">
                {request.vendorName}
              </span>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <CalendarDaysIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
            <span className="text-secondary-600 dark:text-secondary-400">Received:</span>
            <span className="font-medium text-secondary-900 dark:text-white">
              {vendorService.formatDate(request.dateReceived)}
            </span>
          </div>
          {request.targetCompletionDate && (
            <div className="flex items-center space-x-2">
              <CalendarDaysIcon className={cn('w-4 h-4', overdue ? 'text-danger-500' : 'text-secondary-600 dark:text-secondary-400')} />
              <span className={cn(overdue ? 'text-danger-500 font-semibold' : 'text-secondary-600 dark:text-secondary-400')}>
                Target:
              </span>
              <span className={cn('font-medium truncate', overdue ? 'text-danger-600 dark:text-danger-400' : 'text-secondary-900 dark:text-white')}>
                {vendorService.formatDate(request.targetCompletionDate)}
                {overdue && ' (Overdue)'}
              </span>
            </div>
          )}
          {request.assignedHandler && (
            <div className="flex items-center space-x-2">
              <UserIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Handler:</span>
              <span className="font-medium text-secondary-900 dark:text-white truncate">
                {request.assignedHandler}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ContractRequests() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedRequestType, setSelectedRequestType] = useState<string>('all');
  const [selectedUrgency, setSelectedUrgency] = useState<string>('all');
  const [requests, setRequests] = useState<ContractRequest[]>([]);
  const [stats, setStats] = useState<ContractRequestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const [requestsData, statsData] = await Promise.all([
        vendorService.getContractRequests({
          page: currentPage,
          limit: 20,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          requestType: selectedRequestType !== 'all' ? selectedRequestType : undefined,
          urgency: selectedUrgency !== 'all' ? selectedUrgency : undefined,
          search: searchQuery || undefined,
        }),
        vendorService.getContractRequestStats().catch(() => null),
      ]);

      setRequests(requestsData.requests);
      setTotalPages(requestsData.pagination.totalPages);
      setTotalRequests(requestsData.pagination.total);
      if (statsData) {setStats(statsData);}
      setError(null);
    } catch (err) {
      console.error('Failed to fetch contract requests:', err instanceof Error ? err.message : err);
      setError('Failed to load contract requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus, selectedRequestType, selectedUrgency, searchQuery]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Compute stats from current data if stats endpoint is not available
  const computedStats = stats ?? {
    total: totalRequests,
    byStatus: requests.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    byUrgency: {},
    byRequestType: {},
    overdueCount: requests.filter((r) => r.status !== 'COMPLETE' && isOverdue(r.targetCompletionDate)).length,
  };

  const receivedCount = computedStats.byStatus?.['RECEIVED'] ?? 0;
  const inProgressCount = (computedStats.byStatus?.['IN_PROGRESS'] ?? 0) + (computedStats.byStatus?.['IN_REVIEW'] ?? 0);
  const overdueCount = computedStats.overdueCount ?? 0;

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Contract Request Intake
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Track and manage incoming contract requests across your organization
            </p>
          </div>
          <Button className="w-full sm:w-auto" disabled>
            <PlusIcon className="w-4 h-4 mr-2" />
            New Request
          </Button>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {totalRequests}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Total Requests
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">
                {receivedCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Received
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {inProgressCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                In Progress
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {overdueCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Overdue
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search contract requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="RECEIVED">Received</option>
                  <option value="IN_REVIEW">In Review</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="PENDING_SIGNATURES">Pending Signatures</option>
                  <option value="COMPLETE">Complete</option>
                </select>
                <select
                  value={selectedRequestType}
                  onChange={(e) => setSelectedRequestType(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Request Types</option>
                  <option value="NEW_CONTRACT">New Contract</option>
                  <option value="RENEWAL">Renewal</option>
                  <option value="AMENDMENT">Amendment</option>
                  <option value="REVIEW">Review</option>
                  <option value="NDA_REQUEST">NDA Request</option>
                  <option value="RFP_SUPPORT">RFP Support</option>
                  <option value="OTHER">Other</option>
                </select>
                <select
                  value={selectedUrgency}
                  onChange={(e) => setSelectedUrgency(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Urgencies</option>
                  <option value="STANDARD">Standard</option>
                  <option value="EXPEDITED">Expedited</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Contract Requests List */}
      <motion.div variants={ITEM_VARIANTS}>
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading contract requests...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Contract Requests
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error}
              </p>
              <Button onClick={fetchRequests} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : requests.length > 0 ? (
          <div className="space-y-4">
            {requests.map((request) => (
              <ContractRequestCard key={request.id} request={request} />
            ))}

            {/* Pagination */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing {requests.length} of {totalRequests} requests
              </span>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No contract requests found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400">
                No contract requests match your current filters. Try adjusting your search criteria.
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}
